import React from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import Banner from "../components/Banner";
import LegalLinks from "../components/LegalLinks";

type CommandExample = {
  command: string;
  descriptions: string[];
  examples: string[];
};

const commands: CommandExample[] = [
  {
    command: "debit",
    descriptions: [
      "Adds an amount to the debit side of an account.",
      "Use this when the account balance should increase on the debit side.",
      "Options: \n[JE-Id] - specify a journal entry to update, otherwise a new JE-Id will be created. (a, a.1, b, c, etc.)\n",
      "[Comment] - add a comment or description to the entry, wrap in quotes if it contains multiple words.",
    ],
    examples: ["debit Cash 200", "debit 'Accounts Receivable' 1250 a 'Sales on account'"],
  },

  {
    command: "credit",
    descriptions: [
      "Adds an amount to the credit side of an account.",
      "Use this when the account balance should increase on the credit side.",
    ],
    examples: ["credit Revenues 400"],
  },

  {
    command: "bbal",
    descriptions: [
      "Inserts the beginning balance for an account.",
      "Use this to set the opening balance for an account before any debits or credits are applied.",
      "Use delete in place of the amount to remove the account's beginning balance.",
    ],
    examples: ["bbal Cash 1000 debit", "bbal Cash delete"],
  },
  {
    command: "edit",
    descriptions: [
      "Updates an existing journal entry line item.", 
      "Specify the JE-Id of the line item to edit, along with the new details."],
    examples: ["edit a.1 300 debit 'Record cash payment"], 
  },

  {
    command: "move",
    descriptions: ["Moves an existing journal entry line item to another account and optionally change it from a debit to a credit or credit to a debit.",
    "Specify the JE-Id of the line item to move, along with the new account and debit/credit option."],
    examples: ["move a.1 to 'Accounts Receivable' credit'"],
  },

  { 
    command: "title",
    descriptions: [
      "Sets or updates the title for the current worksheet.",
      "Helpful for labeling scenarios, periods, or supporting information.",
    ],
    examples: ["title 'Record month-end journal entries"],
  },

{   command: "rename",
    descriptions: [
      "Renames an existing account to a new name.",
      "Account Names with spaces must be wrapped in quotes.",
    ],

    examples: ["rename Cash to 'Cash in Bank'"],
},

{   command: "comment",
    descriptions: ["Adds a comment or note to a journal entry or journal entry line item."],
    examples: ["comment a 'This is a note'", "comment a.1 'This is a note for line item a.1'"],
  },

  {
    command: "clear",
    descriptions: [
      "Removes all entries from the current worksheet.",
      "Use when you want to start over with a blank prompt state.",
    ],
    examples: ["clear"],
  },
  {
    command: "delete",
    descriptions: [
      "Removes all line items that belong to a top-level JE-Id reference.",
      "Use a single letter only. For example, delete a removes a, a.1, a.2, and so on.",
    ],
    examples: ["delete a"],
  },
  {
    command: "help",
    descriptions: [
      "Opens this Prompt Commands page from the sheet command input.",
      "Use it when you need a quick reference of available command syntax.",
    ],
    examples: ["help"],
  },
];

export default function PromptCommands() {
  React.useEffect(() => {
    let scrollFrame: number | undefined;

    const scrollToHash = () => {
      window.cancelAnimationFrame(scrollFrame ?? 0);
      scrollFrame = window.requestAnimationFrame(() => {
        let commandId: string;
        try {
          commandId = decodeURIComponent(window.location.hash.slice(1));
        } catch {
          return;
        }

        if (commandId) {
          document.getElementById(commandId)?.scrollIntoView({ block: "start" });
        }
      });
    };

    const pendingImages = Array.from(document.images).filter((image) => !image.complete);
    pendingImages.forEach((image) => image.addEventListener("load", scrollToHash));
    window.addEventListener("hashchange", scrollToHash);
    scrollToHash();

    return () => {
      window.cancelAnimationFrame(scrollFrame ?? 0);
      pendingImages.forEach((image) => image.removeEventListener("load", scrollToHash));
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: (theme) => (theme.palette.mode === "light" ? "#f4f6f7" : theme.palette.background.default),
      }}
    >
      <Banner />

      <Container maxWidth="lg" sx={{ pt: 10, pb: 8 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Prompt Commands
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "rgba(255, 255, 255, 0.97)",
            color: "#0f172a",
          }}
        >
          <Typography sx={{ color: "#475467", mb: 3 }}>
            T-account entries are created and managed through simple text commands entered into the prompt input.
            Use these command keywords in the prompt input to create and manage journal entry activity.
          </Typography>

          <Stack spacing={2} sx={{ mb: 4 }}>
            {commands.map((item) => (
              <Paper
                key={item.command}
                id={item.command}
                elevation={0}
                sx={{
                  p: 2,
                  scrollMarginTop: 88,
                  borderRadius: 2,
                  border: "1px solid #d0d5dd",
                  backgroundColor: "#ffffff",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.4, textTransform: "lowercase" }}>
                  {item.command}
                </Typography>

                <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                  {item.descriptions.map((description) => (
                    <Typography
                      key={description}
                      sx={{
                        color: "#344054",
                        whiteSpace: "pre-wrap",
                        tabSize: 4,
                      }}
                    >
                      {description}
                    </Typography>
                  ))}
                </Stack>

                <Stack spacing={0.8}>
                  {item.examples.map((example) => (
                    <Typography
                      key={`${item.command}-${example}`}
                      component="code"
                      sx={{
                        display: "inline-block",
                        width: "fit-content",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                        fontSize: "0.92rem",
                        color: "#0b4f6c",
                        backgroundColor: "#eef8fc",
                        border: "1px solid #c6e8f5",
                      }}
                    >
                      {example}
                    </Typography>
                  ))}
                </Stack>

                {item.command === "move" && (
                  <Stack spacing={0.6} sx={{ mt: 1.5 }}>
                    <Box
                      component="img"
                      src="/move1a.png"
                      alt="Move command example screenshot"
                      sx={{
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        border: "1px solid #d0d5dd",
                      }}
                    />
                  </Stack>
                )}

                {item.command === "title" && (
                  <Stack spacing={0.6} sx={{ mt: 1.5 }}>
                    <Box
                      component="img"
                      src="/Title1a.png"
                      alt="Title command example screenshot"
                      sx={{
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        border: "1px solid #d0d5dd",
                      }}
                    />
                  </Stack>
                )}

                {item.command === "bbal" && (
                  <Stack spacing={0.6} sx={{ mt: 1.5 }}>
                    <Box
                      component="img"
                      src="/bbal1a.png"
                      alt="Bbal command example screenshot"
                      sx={{
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        border: "1px solid #d0d5dd",
                      }}
                    />
                  </Stack>
                )}

                {item.command === "credit" && (
                  <Stack spacing={0.6} sx={{ mt: 1.5 }}>
                    <Box
                      component="img"
                      src="/credit1a.png"
                      alt="Credit command example screenshot"
                      sx={{
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        border: "1px solid #d0d5dd",
                      }}
                    />
                  </Stack>
                )}

                {item.command === "edit" && (
                  <Stack spacing={0.6} sx={{ mt: 1.5 }}>
                    <Box
                      component="img"
                      src="/edit1a.png"
                      alt="Edit command example screenshot"
                      sx={{
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        border: "1px solid #d0d5dd",
                      }}
                    />
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#b54708" }}>
                      Note: Editing an entry with the 'edit' command
                    </Typography>
                  </Stack>
                )}

                {item.command === "debit" && (
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Box
                      component="img"
                      src="/Debit4.png"
                      alt="Debit command example screenshot 4"
                      sx={{
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                        borderRadius: 1.5,
                        border: "1px solid #d0d5dd",
                      }}
                    />
                    <Box sx={{ color: "#b54708" }}>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
                        Note: notice the debit/credit out of balance amount
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Paper>
            ))}
          </Stack>

          <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid", borderColor: "divider", color: "#667085" }}>
            <LegalLinks centered />
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
